// Tenant requirements: what a tenant is looking for (markets, size, timing).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { supabase, unwrap, type Tables } from '../lib/supabase';
import { Button, Empty, ErrorBox, Field, Input, Modal, Pill, Select, Textarea, type PillColor } from './ui';

type Req = Tables<'tenant_requirements'>;
const STATUS_COLOR: Record<string, PillColor> = { active: 'green', satisfied: 'blue', dead: 'gray' };

export function Requirements({ orgId }: { orgId: string }) {
  const [editing, setEditing] = useState<Req | 'new' | null>(null);
  const { data = [] } = useQuery({
    queryKey: ['requirements', orgId],
    queryFn: async () => unwrap(await supabase.from('tenant_requirements').select('*').eq('org_id', orgId).order('created_at', { ascending: false })),
  });
  return (
    <div>
      <div className="mb-2 flex justify-end">
        <Button size="sm" onClick={() => setEditing('new')}>
          + Requirement
        </Button>
      </div>
      {data.length === 0 ? (
        <Empty>No requirements recorded.</Empty>
      ) : (
        <ul className="divide-y divide-slate-100">
          {data.map((r) => (
            <li key={r.id} className="cursor-pointer py-2 text-sm hover:bg-slate-50" onClick={() => setEditing(r)}>
              <div className="flex flex-wrap items-center gap-2">
                <Pill color={STATUS_COLOR[r.status]}>{r.status}</Pill>
                <span className="font-medium text-slate-800">{[...r.target_markets, ...r.target_submarkets].join(', ') || 'Any market'}</span>
                {(r.sf_min || r.sf_max) && (
                  <span className="text-slate-600">
                    {r.sf_min?.toLocaleString() ?? '?'}–{r.sf_max?.toLocaleString() ?? '?'} SF
                  </span>
                )}
                {r.timing && <span className="text-slate-500">· {r.timing}</span>}
              </div>
              {r.notes && <div className="mt-0.5 text-xs text-slate-500">{r.notes}</div>}
            </li>
          ))}
        </ul>
      )}
      <Modal title={editing === 'new' ? 'New requirement' : 'Edit requirement'} open={!!editing} onClose={() => setEditing(null)}>
        {editing && <RequirementForm orgId={orgId} req={editing === 'new' ? undefined : editing} onDone={() => setEditing(null)} />}
      </Modal>
    </div>
  );
}

function RequirementForm({ orgId, req, onDone }: { orgId: string; req?: Req; onDone: () => void }) {
  const qc = useQueryClient();
  const [f, setF] = useState({
    markets: (req?.target_markets ?? []).join(', '),
    submarkets: (req?.target_submarkets ?? []).join(', '),
    sf_min: req?.sf_min?.toString() ?? '',
    sf_max: req?.sf_max?.toString() ?? '',
    use_type: req?.use_type ?? '',
    timing: req?.timing ?? '',
    budget: req?.budget ?? '',
    notes: req?.notes ?? '',
    status: req?.status ?? 'active',
  });
  const list = (s: string) => s.split(',').map((x) => x.trim()).filter(Boolean);
  const save = useMutation({
    mutationFn: async () => {
      const row = {
        org_id: orgId,
        target_markets: list(f.markets),
        target_submarkets: list(f.submarkets),
        sf_min: f.sf_min ? Number(f.sf_min) : null,
        sf_max: f.sf_max ? Number(f.sf_max) : null,
        use_type: f.use_type || null,
        timing: f.timing || null,
        budget: f.budget || null,
        notes: f.notes || null,
        status: f.status,
      };
      if (req) unwrap(await supabase.from('tenant_requirements').update(row).eq('id', req.id));
      else unwrap(await supabase.from('tenant_requirements').insert(row));
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['requirements', orgId] });
      onDone();
    },
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="space-y-3">
      <Field label="Target markets" hint="Cities, comma-separated — used to match buildings (e.g. Alpharetta, Roswell)">
        <Input value={f.markets} onChange={set('markets')} autoFocus />
      </Field>
      <Field label="Submarkets" hint="Comma-separated">
        <Input value={f.submarkets} onChange={set('submarkets')} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="SF min">
          <Input type="number" value={f.sf_min} onChange={set('sf_min')} />
        </Field>
        <Field label="SF max">
          <Input type="number" value={f.sf_max} onChange={set('sf_max')} />
        </Field>
        <Field label="Use">
          <Input value={f.use_type} onChange={set('use_type')} placeholder="Clinical, MOB…" />
        </Field>
        <Field label="Timing">
          <Input value={f.timing} onChange={set('timing')} placeholder="Q2 2027" />
        </Field>
        <Field label="Budget">
          <Input value={f.budget} onChange={set('budget')} />
        </Field>
        <Field label="Status">
          <Select value={f.status} onChange={set('status')}>
            <option value="active">Active</option>
            <option value="satisfied">Satisfied</option>
            <option value="dead">Dead</option>
          </Select>
        </Field>
      </div>
      <Field label="Notes">
        <Textarea value={f.notes} onChange={set('notes')} />
      </Field>
      <ErrorBox error={save.error} />
      <div className="flex justify-end">
        <Button variant="primary" busy={save.isPending} onClick={() => save.mutate()}>
          Save
        </Button>
      </div>
    </div>
  );
}
